import { useState, useEffect } from 'react';
import { pb, Review } from '@/lib/pocketbase';

export const useReviews = (tutorId?: string) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReviews = async (targetTutorId?: string) => {
    const id = targetTutorId || tutorId;
    if (!id) {
      return;
    }

    // Prevent duplicate calls for same tutor
    if (isLoading) return;

    setIsLoading(true);
    setError(null);
    
    try {
      const records = await pb.collection('reviews').getList(1, 50, {
        filter: `tutor = "${id}"`,
        expand: 'studentId',
        sort: '-created',
        requestKey: `reviews-${id}` // Use consistent key for caching
      });
      
      setReviews(records.items as Review[]);
    } catch (err: any) {
      // Don't log expected auth warnings for public data
      if (err.status !== 0 && err.status !== 403 && err.status !== 401) {
        console.error('Error fetching reviews:', err);
      }
      setError('Failed to fetch reviews');
      setReviews([]);
    } finally {
      setIsLoading(false);
    }
  };

  const checkExistingReview = (studentId: string): Review | null => {
    return reviews.find(review => review.studentId === studentId) || null;
  };

  const submitReview = async (reviewData: {
    tutorId: string;
    rating: number;
    comment: string;
    subject: string;
    studentId: string;
  }) => {
    try {
      const record = await pb.collection('reviews').create({
        tutor: reviewData.tutorId,
        studentId: reviewData.studentId,
        rating: reviewData.rating,
        comment: reviewData.comment,
        subject: reviewData.subject,
        isVerified: false
      });
      
      await fetchReviews(reviewData.tutorId);
      return record;
    } catch (err) {
      console.error('Error submitting review:', err);
      throw new Error('Failed to submit review');
    }
  };

  const updateReview = async (reviewId: string, updateData: {
    rating: number;
    comment: string;
    subject: string;
  }) => {
    try {
      const record = await pb.collection('reviews').update(reviewId, {
        rating: updateData.rating,
        comment: updateData.comment,
        subject: updateData.subject,
      });
      
      await fetchReviews(tutorId);
      return record;
    } catch (err) {
      console.error('Error updating review:', err);
      throw new Error('Failed to update review');
    }
  };

  const calculateAverageRating = (reviewList?: Review[]) => {
    const targetReviews = reviewList || reviews;
    if (targetReviews.length === 0) return 0;
    const sum = targetReviews.reduce((acc, review) => acc + review.rating, 0);
    return Number((sum / targetReviews.length).toFixed(1));
  };

  const getRatingDistribution = (reviewList?: Review[]) => {
    const targetReviews = reviewList || reviews;
    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    targetReviews.forEach(review => {
      distribution[review.rating as keyof typeof distribution]++;
    });
    return distribution;
  };

  useEffect(() => {
    // Only fetch if tutorId exists and is different from previous
    if (tutorId) {
      fetchReviews(tutorId);
    }
  }, [tutorId]); // Remove other dependencies that cause excessive re-renders

  return {
    reviews,
    isLoading,
    error,
    fetchReviews,
    submitReview,
    updateReview,
    checkExistingReview,
    calculateAverageRating,
    getRatingDistribution,
  };
};